import { ReactNode } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { Chip, FormRow, StatusSwitcherVariants, Text, usePopoverMenu, useTooltip } from '@frontend/design-system';
import { ChipRightElement } from '../../common';
import { chipStyle, labelContainerStyle, rowStyle, placeholderStyle } from './task-form.styles';

interface TaskFormRowProps {
  children?: ReactNode;
  label: string;
  icon: IconName;
  placeholder?: string;
  buttonIconName: IconName;
  value: string;
  getTriggerProps?: ReturnType<typeof usePopoverMenu<HTMLElement>>['getTriggerProps'];
  tooltipTriggerProps?: ReturnType<typeof useTooltip>['triggerProps'];
  disabled?: boolean;
  variant?: StatusSwitcherVariants;
  isEditing?: boolean;
  leftElement?: React.ReactNode;
  // Keeping this type here because Chip component does not have a defined type for rightElement
  rightElement?: React.ReactNode;
  chipCss?: Interpolation<Theme>;
  onClickRemove?: () => void;
  isRemovable?: boolean;
  error?: string;
  trackingIds?: {
    chipButton?: string;
  };
  isButton?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  showHoverLabel?: boolean;
}

export const TaskFormRow = ({
  children,
  label,
  icon,
  placeholder = '',
  buttonIconName,
  value,
  disabled = false,
  variant = 'default',
  trackingIds,
  leftElement,
  getTriggerProps,
  chipCss,
  onClickRemove,
  isRemovable,
  tooltipTriggerProps,
  isButton = true,
  showHoverLabel = true,
  error = '',
  ...rest
}: TaskFormRowProps) => {
  const { t } = useTranslation('task-center');
  return (
    <>
      <FormRow css={rowStyle} cols={[40]}>
        <div css={labelContainerStyle}>
          <Icon name={icon} size={16} />
          <Text size='medium' weight='bold'>
            {label}
          </Text>
        </div>
        <Chip.StatusSwitcher
          disabled={disabled}
          variant={variant}
          leftElement={leftElement ?? <Icon name={buttonIconName} size={16} />}
          trackingId={trackingIds?.chipButton}
          css={[chipStyle, chipCss, !value && placeholderStyle]}
          tooltipLabel={!isButton && showHoverLabel ? t('Edit {{label}}', { label }) : undefined}
          rightElement={
            <ChipRightElement
              isRemoveable={isRemovable}
              onClickRemove={onClickRemove}
              value={value}
              showRightElement={isButton}
            />
          }
          {...getTriggerProps?.()}
          {...tooltipTriggerProps}
          {...rest}
        >
          {value || placeholder}
        </Chip.StatusSwitcher>
        {children}
      </FormRow>
      {error && (
        <Text color='error' size='small' css={{ marginTop: '-12px', marginBottom: '12px' }}>
          {error}
        </Text>
      )}
    </>
  );
};
